import { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Header } from "../components/layout/Header";
import { marketplaceApi, pluginApi, type Plugin, type MarketplaceDetail } from "../lib/api";
import { SearchIcon, ArrowLeftIcon, CheckIcon } from "../components/ui/Icons";

export function MarketplaceEditPage() {
  const { id } = useParams<{ id: string }>();
  const [marketplace, setMarketplace] = useState<MarketplaceDetail | null>(null);
  const [allPlugins, setAllPlugins] = useState<Plugin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  const selectedPluginIds = useMemo(() => {
    if (!marketplace) return new Set<string>();
    return new Set(marketplace.plugins.map((p) => p.pluginId));
  }, [marketplace]);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        const [mp, plugins] = await Promise.all([
          marketplaceApi.getMarketplace(id),
          pluginApi.getPublishedPlugins(),
        ]);
        setMarketplace(mp);
        setAllPlugins(plugins);
      } catch (err) {
        console.error("Failed to load data:", err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id]);

  const filteredPlugins = useMemo(() => {
    if (!search.trim()) return allPlugins;
    const q = search.toLowerCase();
    return allPlugins.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [allPlugins, search]);

  const togglePlugin = async (pluginId: string) => {
    if (!id || !marketplace || togglingIds.has(pluginId)) return;

    setTogglingIds((prev) => new Set(prev).add(pluginId));

    try {
      const isSelected = selectedPluginIds.has(pluginId);

      if (isSelected) {
        await marketplaceApi.removePlugin(id, pluginId);
        setMarketplace((prev) =>
          prev
            ? {
                ...prev,
                plugins: prev.plugins.filter((p) => p.pluginId !== pluginId),
              }
            : prev
        );
      } else {
        const nextOrder = marketplace.plugins.length;
        await marketplaceApi.addPlugin(id, pluginId, nextOrder);
        setMarketplace((prev) =>
          prev
            ? {
                ...prev,
                plugins: [...prev.plugins, { pluginId, order: nextOrder }],
              }
            : prev
        );
      }
    } catch (err) {
      console.error("Failed to toggle plugin:", err);
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(pluginId);
        return next;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-base">
        <Header />
        <main className="relative pt-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="animate-pulse">
              <div className="mb-6 h-8 w-48 rounded bg-elevated/50" />
              <div className="mb-8 h-10 w-full rounded-lg bg-elevated/50" />
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="card-glow animate-pulse p-6">
                    <div className="mb-3 h-5 w-40 rounded bg-elevated/50" />
                    <div className="mb-2 h-3 w-full rounded bg-elevated/50" />
                    <div className="h-3 w-2/3 rounded bg-elevated/50" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!marketplace) {
    return (
      <div className="min-h-screen bg-base">
        <Header />
        <main className="relative pt-24">
          <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
            <h1 className="font-display text-2xl font-bold text-primary">Marketplace not found</h1>
            <Link
              to="/dashboard/marketplaces"
              className="mt-4 inline-flex items-center gap-2 text-sm text-amber hover:underline"
            >
              <ArrowLeftIcon size={16} />
              Back to Marketplaces
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base">
      <Header />

      {/* Background effects */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-1/4 top-0 h-[600px] w-[600px] rounded-full bg-amber/5 blur-[120px]" />
        <div className="absolute -left-1/4 bottom-0 h-[600px] w-[600px] rounded-full bg-purple-500/5 blur-[120px]" />
        <div className="absolute inset-0 bg-grid opacity-20" />
      </div>

      <main className="relative pt-24">
        <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
          {/* Back link */}
          <Link
            to="/dashboard/marketplaces"
            className="mb-6 inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-secondary"
          >
            <ArrowLeftIcon size={16} />
            Back to Marketplaces
          </Link>

          {/* Header */}
          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold tracking-tight text-primary">
              {marketplace.name}
            </h1>
            {marketplace.description && (
              <p className="mt-2 text-muted">{marketplace.description}</p>
            )}
            <p className="mt-2 font-mono text-sm text-amber">
              {selectedPluginIds.size} {selectedPluginIds.size === 1 ? "plugin" : "plugins"}{" "}
              selected
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-8">
            <SearchIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search plugins by name, description, or tag..."
              className="w-full rounded-xl border border-elevated bg-surface/50 py-3 pl-12 pr-4 font-body text-sm text-primary placeholder-muted outline-none transition-colors focus:border-amber/50 focus:ring-1 focus:ring-amber/20"
            />
          </div>

          {/* Plugin Grid */}
          {filteredPlugins.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-muted">No plugins found matching your search.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredPlugins.map((plugin) => {
                const isSelected = selectedPluginIds.has(plugin.id);
                const isToggling = togglingIds.has(plugin.id);

                return (
                  <button
                    key={plugin.id}
                    onClick={() => togglePlugin(plugin.id)}
                    disabled={isToggling}
                    className={`card-glow p-6 text-left transition-all ${
                      isSelected ? "ring-2 ring-amber/50" : "hover:ring-1 hover:ring-elevated"
                    } ${isToggling ? "opacity-60" : ""}`}
                  >
                    <div className="mb-3 flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                            isSelected ? "bg-amber/20 ring-1 ring-amber/30" : "bg-elevated/50"
                          }`}
                        >
                          {isSelected ? (
                            <CheckIcon size={18} className="text-amber" />
                          ) : (
                            <span className="font-display text-sm font-bold text-muted">
                              {plugin.name.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <h3 className="truncate font-display text-sm font-bold text-primary">
                            {plugin.name}
                          </h3>
                          <p className="font-mono text-xs text-muted">by {plugin.author.name}</p>
                        </div>
                      </div>
                    </div>
                    <p className="line-clamp-2 text-sm text-secondary">{plugin.description}</p>
                    {plugin.tags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {plugin.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="rounded-md bg-elevated/50 px-2 py-0.5 font-mono text-xs text-muted"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
